import React, { useRef, useMemo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import AddIcon from '@atlaskit/icon/core/add';
import { Flex } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectsAnalyticsL2 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l2/index.tsx';
import { CommonNavMenuButton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/main.tsx';
import { ProjectActionMenu } from '@atlassian/jira-navigation-project-action-menu/src/ui/index.tsx';
import type {
	ProjectActions$data,
	ProjectActions$key,
} from '@atlassian/jira-relay/src/__generated__/ProjectActions.graphql';
import { BoardCreate } from '@atlassian/jira-software-board-create/src/ui/index.tsx';
import { ThemePicker } from '@atlassian/jira-project-theme-components/src/ui/theme-picker/ThemePicker.tsx';
import {
	getIsSoftwareThemingEnabled,
	getIsSoftwareThemingM2Enabled,
} from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';

import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { isCompanyManagedSoftwareProject } from './utils.tsx';
import messages from './messages.tsx';

export type Props = {
	projectFragment: ProjectActions$key;
	isCorePremiumUserSeat?: boolean;
};

export const useRouterMatch = createRouterSelector((state) => state.match);

const Trigger = ({
	triggerRef,
	isFavourite,
	projectId,
	projectType,
	setCreateBoardButtonRef,
	...props
}: CustomTriggerProps & {
	isFavourite: boolean;
	projectId: string;
	projectType: ProjectActions$data['projectType'];
	setCreateBoardButtonRef: (instance: HTMLButtonElement | null) => void;
}) => {
	const { formatMessage } = useIntl();
	const { sendCreateBoardOnBoardMenuItemAnalyticsEvent } = useProjectsAnalyticsL2();
	// callback to set button element to local ref.
	// to be used as prop for setting focus back to button after modal close.
	const setRef = useCallback(
		(buttonElement: HTMLButtonElement | null) => {
			setCreateBoardButtonRef(buttonElement);
			if (typeof triggerRef === 'function') {
				triggerRef(buttonElement);
			}
		},
		[setCreateBoardButtonRef, triggerRef],
	);
	const onCommonNavMenuButtonClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			const { onClick } = props;
			onClick?.(event);
			sendCreateBoardOnBoardMenuItemAnalyticsEvent({
				starred: isFavourite ?? false,
				recent: !isFavourite,
				projectId,
				projectType,
			});
		},
		[isFavourite, projectId, projectType, props, sendCreateBoardOnBoardMenuItemAnalyticsEvent],
	);

	return (
		<Tooltip content={formatMessage(messages.createBoard)}>
			<CommonNavMenuButton
				{...props}
				onClick={onCommonNavMenuButtonClick}
				icon={AddIcon}
				label={formatMessage(messages.createBoard)}
				ref={setRef}
			/>
		</Tooltip>
	);
};
export const ProjectActions = ({ projectFragment, isCorePremiumUserSeat = false }: Props) => {
	const { formatMessage } = useIntl();
	const {
		sendMeatBallDropDownOnProjectsAnalyticsEvent,
		sendCreateBoardOnBoardMenuItemAnalyticsEvent,
	} = useProjectsAnalyticsL2();
	const match = getIsSoftwareThemingEnabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useRouterMatch()
		: undefined;
	const createBoardButtonRef = useRef<HTMLButtonElement | null>(null);
	const { hasSoftwareTheming } = getIsSoftwareThemingEnabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { hasSoftwareTheming: false };

	const project = useFragment<ProjectActions$key>(
		graphql`
			fragment ProjectActions on JiraProject
			@argumentDefinitions(
				checkForA11YEnabledFF: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsm-aiiy-for-nav-item-sidebar.relayprovider"
				}
			) {
				name @include(if: $checkForA11YEnabledFF)
				projectKey: key @required(action: THROW)
				projectId @required(action: THROW)
				isFavourite
				projectStyle
				projectType @required(action: THROW)
				avatar {
					large
				}
				editPermission: action(type: EDIT_PROJECT_CONFIG) {
					canPerform
				}
			}
		`,
		projectFragment,
	);

	const onProjectActionMenuClick = useCallback(() => {
		sendMeatBallDropDownOnProjectsAnalyticsEvent({
			starred: project.isFavourite ?? false,
			recent: !project.isFavourite,
			projectId: project.projectId,
			projectType: project.projectType,
		});
	}, [
		project.isFavourite,
		project.projectId,
		project.projectType,
		sendMeatBallDropDownOnProjectsAnalyticsEvent,
	]);

	const canChangeTheme = useMemo(() => {
		const canEditProjectConfig = project?.editPermission?.canPerform ?? false;
		const currentProjectKey = match?.params?.projectKey || match?.params?.issueKey?.split('-')[0];
		return currentProjectKey === project.projectKey && canEditProjectConfig && hasSoftwareTheming;
	}, [
		hasSoftwareTheming,
		match?.params?.issueKey,
		match?.params?.projectKey,
		project?.editPermission?.canPerform,
		project.projectKey,
	]);

	const renderAdditionalMenuItems = useCallback(() => {
		return (
			<>
				{canChangeTheme && !getIsSoftwareThemingM2Enabled() && getIsSoftwareThemingEnabled() && (
					<ThemePicker projectId={Number(project.projectId)} withAdaptiveColorTheme />
				)}
			</>
		);
	}, [canChangeTheme, project.projectId]);

	const projectActionMenu = useMemo(() => {
		return (
			<ProjectActionMenu
				{...(fg('jsm_aiiy_for_nav_item_sidebar') ? { projectName: project.name } : {})}
				onClick={
					fg('jfp-magma-stability-check-nav4-batch-1')
						? onProjectActionMenuClick
						: () => {
								sendMeatBallDropDownOnProjectsAnalyticsEvent({
									starred: project.isFavourite ?? false,
									recent: !project.isFavourite,
									projectId: project.projectId,
									projectType: project.projectType,
								});
							}
				}
				renderAdditionalMenuItems={
					fg('jfp-magma-stability-check-nav4-batch-1')
						? renderAdditionalMenuItems
						: () => (
								<>
									{canChangeTheme &&
										!getIsSoftwareThemingM2Enabled() &&
										getIsSoftwareThemingEnabled() && (
											<ThemePicker projectId={Number(project.projectId)} withAdaptiveColorTheme />
										)}
								</>
							)
				}
				projectKey={project.projectKey}
				isCorePremiumUserSeat={isCorePremiumUserSeat}
				{...(canChangeTheme &&
					getIsSoftwareThemingM2Enabled() && {
						themePicker: (
							<ThemePicker projectId={Number(project.projectId)} withAdaptiveColorTheme />
						),
					})}
				isProjectMenuReorderingEnabled={
					project?.isFavourite === true &&
					expVal('blu-6314-drag-and-drop-projects-sidebar-experiment', 'isEnabled', false)
				}
			/>
		);
	}, [
		project.name,
		project.projectKey,
		project.projectId,
		project.isFavourite,
		project.projectType,
		onProjectActionMenuClick,
		renderAdditionalMenuItems,
		isCorePremiumUserSeat,
		canChangeTheme,
		sendMeatBallDropDownOnProjectsAnalyticsEvent,
	]);

	const setCreateBoardButtonRef = useCallback(
		(instance: HTMLButtonElement | null) => {
			createBoardButtonRef.current = instance;
		},
		[createBoardButtonRef],
	);
	const renderTrigger = useCallback(
		(props: CustomTriggerProps) => (
			<Trigger
				{...props}
				isFavourite={project.isFavourite ?? false}
				projectId={project.projectId}
				projectType={project.projectType}
				setCreateBoardButtonRef={setCreateBoardButtonRef}
			/>
		),
		[project.isFavourite, project.projectId, project.projectType, setCreateBoardButtonRef],
	);
	return (
		<>
			<Flex gap="space.050" alignItems="center">
				{isCompanyManagedSoftwareProject(project.projectStyle, project.projectType) && (
					<BoardCreate
						projectId={project.projectId}
						projectAvatar={project.avatar?.large || undefined}
						triggerRef={createBoardButtonRef}
						renderTrigger={
							fg('jfp-magma-stability-check-nav4-batch-1')
								? renderTrigger
								: ({ triggerRef, ...props }) => {
										// callback to set button element to local ref.
										// to be used as prop for setting focus back to button after modal close.
										const setRef = (buttonElement: HTMLButtonElement | null) => {
											createBoardButtonRef.current = buttonElement;
											if (typeof triggerRef === 'function') {
												triggerRef(buttonElement);
											}
										};
										return (
											<Tooltip content={formatMessage(messages.createBoard)}>
												<CommonNavMenuButton
													{...props}
													onClick={(event) => {
														const { onClick } = props;
														onClick?.(event);
														sendCreateBoardOnBoardMenuItemAnalyticsEvent({
															starred: project.isFavourite ?? false,
															recent: !project.isFavourite,
															projectId: project.projectId,
															projectType: project.projectType,
														});
													}}
													icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
													label={formatMessage(messages.createBoard)}
													ref={setRef}
												/>
											</Tooltip>
										);
									}
						}
					/>
				)}
			</Flex>
			{projectActionMenu}
		</>
	);
};
